import { getStorage, setStorage } from '@/utils/storage'
import { request } from '@/utils/request'

/**
 * 角色 id（接口字段 id，即登录存的 roleId）
 * 系统管理员 1 / 销售 2 / 代理商 3 / 运营 4
 * 一级代理 1900000000000000001 / 二级代理 1900000000000000002
 * 混显号运营商 1900000000000000003（lineOpMix）/ 自备号运营商 1900000000000000004
 */
export const ROLE_ADMIN = '1'
export const ROLE_SALES = '2'
export const ROLE_AGENT = '3'
export const ROLE_OPS = '4'
export const ROLE_L1_AGENT = '1900000000000000001'
export const ROLE_L2_AGENT = '1900000000000000002'
export const ROLE_LINE_OP_MIX = '1900000000000000003' // 混显号运营商 lineOpMix
export const ROLE_LINE_OP_OWN = '1900000000000000004' // 自备号运营商

export const ROLES_ADMIN = [ROLE_ADMIN]
export const ROLES_ADMIN_OR_AGENT = [ROLE_ADMIN, ROLE_AGENT, ROLE_L1_AGENT, ROLE_L2_AGENT]
export const ROLES_ADMIN_OR_OPS = [ROLE_ADMIN, ROLE_OPS]
export const ROLES_SYSTEM_USER = [ROLE_ADMIN, ROLE_L1_AGENT, ROLE_L2_AGENT]
export const ROLES_OPEN_USER = [ROLE_ADMIN, ROLE_AGENT, ROLE_OPS, ROLE_L1_AGENT, ROLE_L2_AGENT]
export const ROLES_CUSTOMER = [
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_AGENT,
  ROLE_OPS,
  ROLE_L1_AGENT,
  ROLE_L2_AGENT
]
/** 线路申请：管理员 + 混显号/自备号运营商 */
export const ROLES_LINE = [ROLE_ADMIN, ROLE_LINE_OP_MIX, ROLE_LINE_OP_OWN]
/** 仅线路运营商（不含管理员） */
export const ROLES_LINE_OP_ONLY = [ROLE_LINE_OP_MIX, ROLE_LINE_OP_OWN]

const ROLE_LIST_KEY = 'roleList'
const ROLE_LIST_SKIP_KEY = 'roleListSkip'

export function getCachedRoleList () {
  try {
    return JSON.parse(getStorage(ROLE_LIST_KEY) || '[]') || []
  } catch (e) {
    return []
  }
}

/** 当前用户是否仅为线路运营商（无需高级筛选字典 / 角色列表） */
export function isLineOperatorOnly () {
  const userRoles = getUserRoles()
  if (!userRoles.length) return false
  if (userRoles.indexOf(ROLE_ADMIN) !== -1) return false
  return userRoles.every(id => ROLES_LINE_OP_ONLY.indexOf(String(id)) !== -1)
}

/** 拉取并缓存角色列表 */
export function loadRoleList () {
  if (isLineOperatorOnly()) {
    setStorage(ROLE_LIST_KEY, '[]')
    setStorage(ROLE_LIST_SKIP_KEY, '1')
    return Promise.resolve([])
  }
  return request('/api/sysrole/rolename', {}, false).then((res) => {
    const list = res.data || []
    setStorage(ROLE_LIST_KEY, JSON.stringify(list))
    setStorage(ROLE_LIST_SKIP_KEY, '')
    return list
  }).catch((err) => {
    // 无权限时标记跳过，避免路由守卫反复重试
    setStorage(ROLE_LIST_SKIP_KEY, '1')
    setStorage(ROLE_LIST_KEY, '[]')
    return Promise.reject(err)
  })
}

export function shouldSkipRoleList () {
  return getStorage(ROLE_LIST_SKIP_KEY) === '1' || isLineOperatorOnly()
}

export function getUserRoles () {
  return String(getStorage('roleId') || '')
    .split(',')
    .map(item => String(item).trim())
    .filter(Boolean)
}

/** @param {string[]} roles 角色 id 列表 */
export function hasPermission (roles) {
  if (!roles || !roles.length) return false
  const userRoleIds = getUserRoles()
  if (!userRoleIds.length) return false
  return roles.some(id => userRoleIds.indexOf(String(id)) !== -1)
}

/**
 * 当前登录用户是否可新建系统用户（二级代理不可新建）
 */
export function canCreateAgentUser () {
  if (hasPermission(ROLES_ADMIN)) return true
  if (hasPermission([ROLE_L1_AGENT])) return true
  return false
}

/**
 * 可创建/筛选的角色（按基准角色判断）
 * - 管理员：不显示 1/2/3/4，仅一二级代理
 * - 一级代理：仅二级代理
 * @param {Array} list 角色列表
 * @param {string|number} [baseRoleId] 基准角色；不传则按当前登录用户
 */
export function filterAssignableRoles (list = [], baseRoleId) {
  const roles = list || []
  let viewerRole = baseRoleId != null && baseRoleId !== '' ? String(baseRoleId) : ''
  if (!viewerRole) {
    const userRoles = getUserRoles()
    if (userRoles.indexOf(ROLE_ADMIN) !== -1) viewerRole = ROLE_ADMIN
    else if (userRoles.indexOf(ROLE_L1_AGENT) !== -1) viewerRole = ROLE_L1_AGENT
    else if (userRoles.indexOf(ROLE_L2_AGENT) !== -1) viewerRole = ROLE_L2_AGENT
    else viewerRole = userRoles[0] || ''
  }

  if (viewerRole === ROLE_ADMIN) {
    const hideIds = [ROLE_ADMIN, ROLE_SALES, ROLE_AGENT, ROLE_OPS]
    return roles.filter(item => !hideIds.includes(String(item.id)))
  }
  if (viewerRole === ROLE_L1_AGENT) {
    return roles.filter(item => String(item.id) === ROLE_L2_AGENT)
  }
  return []
}

// 兼容旧调用
export const loadRoleIdMap = loadRoleList
export const loadRoleTypeIdMap = loadRoleList
export const resolveRoleIds = (ids = []) => (ids || []).map(id => String(id)).filter(Boolean)
export const getRoleIdMap = () => ({})
export const getRoleTypeIdMap = getRoleIdMap
export const getRoleCodeIdMap = getRoleIdMap



// WEBPACK FOOTER //
// ./src/utils/permission.js